import React, { useState, useEffect, useMemo } from "react";
import UpDownArrow from "../assets/ic_updown.svg";
import placeholder from "../assets/placeholder.svg";
import config from "../config";

const UserList = () => {
  const [userList, setUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    user: null,
    action: null,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}/get/alluser`);
      const data = await res.json();
      if (data.success) {
        setUserList(data.data);
      } else {
        alert("Failed to load users");
      }
    } catch (error) {
      console.error(error);
      alert("Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  const requestBlockToggle = (user) => {
    setConfirmDialog({
      open: true,
      user,
      action: user.blockStatus === 1 ? "unblock" : "block",
    });
  };

  const confirmBlockToggle = async () => {
    const { user } = confirmDialog;
    if (!user) return;

    try {
      const res = await fetch(`${config.BASE_URL}/block-toggle/${user.id}`, {
        method: "PATCH",
      });

      const data = await res.json();
      if (data.success) {
        setUserList((prev) =>
          prev.map((u) =>
            u.id === user.id ? { ...u, blockStatus: data.data.blockStatus } : u
          )
        );
        alert(
          data.data.blockStatus === 1
            ? "User blocked successfully."
            : "User unblocked successfully."
        );
      } else {
        alert("Failed to update block status");
      }
    } catch (err) {
      console.error(err);
      alert("Error updating block status");
    } finally {
      setConfirmDialog({ open: false, user: null, action: null });
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return userList;
    const lowerSearch = searchTerm.toLowerCase();
    return userList.filter(
      (user) =>
        user.name?.toLowerCase().includes(lowerSearch) 
    );
  }, [searchTerm, userList]);

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredUsers;
    const sorted = [...filteredUsers].sort((a, b) => {
      let aVal = a[sortConfig.key]?.toString().toLowerCase() || "";
      let bVal = b[sortConfig.key]?.toString().toLowerCase() || "";
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredUsers, sortConfig]);

  const totalEntries = sortedUsers.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPage;
    return sortedUsers.slice(start, start + entriesPerPage);
  }, [sortedUsers, currentPage, entriesPerPage]);

  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const headers = [
    { label: "Full Name", key: "name" },
    { label: "Email", key: "email" },
  ];

  return (
    <div className="p-6 bg-[#211436] min-h-screen font-sans relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg flex items-center space-x-3">
            <svg
              className="animate-spin h-6 w-6 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <span className="text-blue-600 font-semibold text-lg">
              Loading...
            </span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mb-4 flex flex-wrap justify-between items-center gap-3 bg-[#351F57] rounded-[6px] px-4 py-3">
      

        <input
          type="search"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none bg-[#351F57]"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto p-3 bg-[#351F57] rounded-[6px] border border-transparent">
        <table className="w-full text-left text-sm text-white table-fixed border border-gray-200">
          <thead className="bg-[#351F57] border-b border-gray-300">
            <tr className="divide-x divide-gray-300">
              {headers.map(({ label, key }) => (
                <th key={key} className="p-4 select-none">
                  <div className="inline-flex items-center space-x-2">
                    <span>{label}</span>
                    <img
                      src={UpDownArrow}
                      alt="Sort"
                      className={`w-4 h-4 cursor-pointer transition-transform ${
                        sortConfig.key === key
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : "rotate-0"
                          : ""
                      }`}
                      onClick={() => handleSort(key)}
                    />
                  </div>
                </th>
              ))}
              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user, index) => (
                <tr
                  key={user.id}
                  className={`divide-x divide-gray-300 ${
                    index % 2 === 0 ? "bg-[#482772]" : "bg-[#351F57]"
                  }`}
                >
                  {/* Name */}
                  <td className="p-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                    
                      <p className="font-semibold text-white">
                        {user.name || "-"}
                      </p>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="p-4 text-white break-words">
                    {user.email || "-"}
                  </td>

                  {/* Action Buttons */}
                  <td className="p-4 text-center">
                    <button
                      onClick={() => requestBlockToggle(user)}
                      className={`px-4 py-1 rounded-full font-medium ${
                        user.blockStatus === 1
                          ? "bg-red-400 text-white hover:bg-red-300"
                          : "bg-red-600 text-white hover:bg-red-700"
                      }`}
                    >
                      {user.blockStatus === 1 ? "Unblock" : "Block"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between text-white text-sm space-y-2 md:space-y-0">
          <div className="text-white font-bold">
            Showing{" "}
            {totalEntries === 0
              ? 0
              : (currentPage - 1) * entriesPerPage + 1}{" "}
            to {Math.min(currentPage * entriesPerPage, totalEntries)} of{" "}
            {totalEntries} entries
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1 rounded-full hover:bg-black disabled:opacity-50"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`px-3 py-1 rounded ${
                  page === currentPage
                    ? "text-white"
                    : "hover:bg-gray-200"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded-full hover:bg-black disabled:opacity-50"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div className="px-6 py-4">
              <h2 className="text-lg font-bold text-gray-800">
                {confirmDialog.action === "block" ? "Block User" : "Unblock User"}
              </h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600">
                {confirmDialog.action === "block"
                  ? `Are you sure you want to block this user?`
                  : `Are you sure you want to unblock this user?`}
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() =>
                  setConfirmDialog({ open: false, user: null, action: null })
                }
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmBlockToggle}
                className="px-4 py-2 bg-[#000000] text-white rounded-md hover:bg-gray-800"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
